// #772 part 3 -- smart alignment guides (docs/model/drawing-ui-viewport.md). While placing or
// dragging, a point lines up with what is already on the paper: each axis snaps on its own to the
// nearest guide within 8 CSS px (whatever the zoom), else to the grid when Snap to grid is on.
// Guides come from atoms (their x and y), the edges and centre of each annotation, and the page's
// edges and centre. Ties: the nearer target wins; if exactly as near, the topmost object.
// Everything here is in drawing units.

export const GUIDE_SNAP_DISTANCE_PX = 8;

export interface GuideCandidate {
  readonly value: number;
  // What the guide belongs to, so a drag never aligns an item with itself ('page' for the page).
  readonly owner: string;
  // Stacking order: higher is on top (page < atoms < annotations, later annotations higher).
  readonly z: number;
}

export interface GuideSources {
  readonly xs: readonly GuideCandidate[];
  readonly ys: readonly GuideCandidate[];
}

interface Point {
  readonly x: number;
  readonly y: number;
}

export function collectGuideSources(
  // The page's rectangle (#805: it may have grown left or up, so it need not start at 0, 0).
  page: { readonly x: number; readonly y: number; readonly width: number; readonly height: number },
  atoms: readonly { readonly id: string; readonly position: Point }[],
  annotations: readonly { readonly id: string; readonly extent: readonly Point[] }[]
): GuideSources {
  const xs: GuideCandidate[] = [];
  const ys: GuideCandidate[] = [];
  for (const value of [page.x, page.x + page.width / 2, page.x + page.width]) xs.push({ value, owner: 'page', z: -1 });
  for (const value of [page.y, page.y + page.height / 2, page.y + page.height])
    ys.push({ value, owner: 'page', z: -1 });
  for (const atom of atoms) {
    xs.push({ value: atom.position.x, owner: atom.id, z: 0 });
    ys.push({ value: atom.position.y, owner: atom.id, z: 0 });
  }
  annotations.forEach((annotation, index) => {
    if (annotation.extent.length === 0) return;
    const pointXs = annotation.extent.map((point) => point.x);
    const pointYs = annotation.extent.map((point) => point.y);
    const [minX, maxX, minY, maxY] = [
      Math.min(...pointXs),
      Math.max(...pointXs),
      Math.min(...pointYs),
      Math.max(...pointYs)
    ];
    const z = index + 1;
    for (const value of [minX, (minX + maxX) / 2, maxX]) xs.push({ value, owner: annotation.id, z });
    for (const value of [minY, (minY + maxY) / 2, maxY]) ys.push({ value, owner: annotation.id, z });
  });
  return { xs, ys };
}

export function nearestGuide(
  value: number,
  candidates: readonly GuideCandidate[],
  maxDistance: number,
  exclude?: ReadonlySet<string>
): GuideCandidate | undefined {
  let best: GuideCandidate | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    if (exclude?.has(candidate.owner)) continue;
    const distance = Math.abs(candidate.value - value);
    if (distance > maxDistance) continue;
    if (distance < bestDistance || (distance === bestDistance && best && candidate.z > best.z)) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}

export interface SnapResult {
  readonly x: number;
  readonly y: number;
  // The guide each axis aligned to, for drawing the guide line.
  readonly guideX?: number;
  readonly guideY?: number;
  // Whether anything moved the point (a guide or the grid), for the target marker.
  readonly snapped: boolean;
}

export function resolveSnap(
  point: Point,
  options: {
    readonly guides: GuideSources | null;
    readonly gridSpacing: number | null;
    readonly maxDistance: number;
    readonly exclude?: ReadonlySet<string>;
  }
): SnapResult {
  const { guides, gridSpacing, maxDistance, exclude } = options;
  const axis = (value: number, candidates: readonly GuideCandidate[] | undefined) => {
    const guide = candidates ? nearestGuide(value, candidates, maxDistance, exclude) : undefined;
    if (guide) return { value: guide.value, guide: guide.value };
    if (gridSpacing && gridSpacing > 0) return { value: Math.round(value / gridSpacing) * gridSpacing };
    return { value };
  };
  const x = axis(point.x, guides?.xs);
  const y = axis(point.y, guides?.ys);
  return {
    x: x.value,
    y: y.value,
    ...(x.guide !== undefined ? { guideX: x.guide } : {}),
    ...(y.guide !== undefined ? { guideY: y.guide } : {}),
    snapped: x.value !== point.x || y.value !== point.y
  };
}
